// adminSettings.mjs — /admin/settings 的纯逻辑（SB-068/SB-077/SB-084，第六十八批）。
//
// 页面本身只做渲染；每一条判断都在这里，因为它们都是**会出错而且错得很安静**的判断：
//   · 一个型号能不能给某个步骤用（视觉步骤要视觉能力）；
//   · 一条覆盖是"生效了"还是"存着但被忽略"（后者是 admin 最容易误解的状态：
//     页面显示配置值，他以为自己没保存成功，而其实存住了、只是模型已不可路由）；
//   · 目录是新鲜的还是陈旧的。
// 纯函数 ⇒ 可穷举单测，而这三件事**在界面上都长得差不多**。

// 后端投影里每个 operation 的三个值（routes_admin.cpp）：
//   configModel     部署值（stage jsonc 合并出厂默认）
//   overrideValue   运行期覆盖（meta 表；null = 没设过）
//   effectiveModel  实际生效值
// overrideIgnored = 覆盖存在但没生效（模型已不在 modelMappings 里）。
export const OP_ORDER = ['ticket_review', 'translate', 'polish', 'asset_review', 'chat'];

// 展示顺序刻意按"这一步有多重要"排，而不是字母序：
// ticket_review / translate 是无人值守、每天跑的；chat 只是冒烟路由。
export function sortOperations(ops) {
  const rank = (o) => {
    const i = OP_ORDER.indexOf(o.op);
    return i < 0 ? OP_ORDER.length : i;
  };
  return [...(ops ?? [])].sort((a, b) => rank(a) - rank(b) || String(a.op).localeCompare(b.op));
}

// 一条 operation 的状态。三态互斥，且**每一态都要在界面上看得出来**：
//   'default'  没设覆盖，用部署值
//   'override' 覆盖生效中
//   'ignored'  覆盖存着但没生效 ← 最危险的一态：不显式标出来就等于没存
export function overrideState(op) {
  if (op?.overrideIgnored) return 'ignored';
  if (op?.overrideValue !== null && op?.overrideValue !== undefined) return 'override';
  return 'default';
}

// 某个型号能不能给这个步骤用。
// kind==='vision' 的步骤要求型号有视觉能力；而**分类不出能力的型号（known=false）
// 一律当作没有**——判不出来时选择保守（model_catalog.h 的总纪律）。
export function isSelectable(model, op) {
  if (!model || !op) return false;
  // 第六十九批收紧：**能力未知的一律禁选**，不再只对视觉步骤禁。
  // 后端 validate 的目录门槛是 known 且 structured——界面若比它宽，
  // 选了就是 400"保存失败"，而那正是本批要消灭的形状（界面比接口宽，DP-2）。
  if (!model.known) return false;
  if (op.kind === 'vision') return Boolean(model.vision);
  return true;
}

// 给某个步骤的候选列表：可选的在前，不可选的仍然显示但禁用并给出理由——
// **把选项藏起来会让人以为系统坏了**，而带理由的禁用项会让人知道该去改什么。
export function optionsFor(models, op) {
  return (models ?? []).map((m) => ({
    ...m,
    selectable: isSelectable(m, op),
    reason: isSelectable(m, op) ? '' : (m.known ? 'noVision' : 'unknown'),
  }));
}

// 目录新鲜度。fetchedAtMs 为 null ⇒ 从未拉取过（mock 姿态或刚部署完还没跑过作业）。
// 判据与 ticket-closeout 那条同款：**"没能检查"不与"检查过且是新的"同色**。
export function catalogFreshness(fetchedAtMs, nowMs, staleHours = 24) {
  if (fetchedAtMs === null || fetchedAtMs === undefined) return { state: 'never', ageMs: null };
  const age = nowMs - fetchedAtMs;
  return { state: age > staleHours * 3600_000 ? 'stale' : 'fresh', ageMs: age };
}

// 提交前的守卫：想设的值与当前生效值相同就别发请求——
// 一次什么都不改的写入会在审计里留下一条"改过"，而那是假的。
export function needsWrite(op, nextModel) {
  const cur = overrideState(op) === 'default' ? '' : (op.overrideValue ?? '');
  return String(nextModel ?? '') !== String(cur);
}

// ── 第一百一十批 B：作业的失败分类与重排 ────────────────────────────────────
//
// ⚠ **`''` 必须被读成「不知道」，不是「正常」**。本批之前的存量失败行（dev 上那条
// 113 号就是）全是空串；把它折进 transient，页面就会对着一条其实是 400 的失败说
// "多半是网络或供应商抖动，重试看看"——而那是三次徒劳重试的邀请。所以
// `failClass.unknown` 是独立的一句，不是 transient 的别名。
// 第二百三十一批新增 `account`：账户此刻不让用（余额／配额／密钥失效）。它**不要**
// force——重排正是它的正解，人把账户修好之后原样再跑一次就该成，这与 permanent
// 「再排一条一模一样的必然同样被拒」是相反的情形。
export function failClassOf(row) {
  const c = row && row.resultClass;
  return c === 'transient' || c === 'permanent' || c === 'account' ? c : 'unknown';
}

export const failClassKey = (row) => `admin.settings.jobs.failClass.${failClassOf(row)}`;

// 重排这条要不要 force。**只有 permanent 要**：permanent 的意思是「自动退避重试
// 徒劳」（400/401/403/404/413/422 —— 请求本身被拒），再排一条一模一样的必然同样被
// 拒。`unknown` 不要 force：它是"不知道"，而对一件不知道的事要求一次额外确认，
// 只会让人学会无脑按那个确认。
export const requeueNeedsForce = (row) => failClassOf(row) === 'permanent';

export const REQUEUE_ERR_KEYS = Object.freeze({
  JOB_NOT_TERMINAL: 'admin.settings.jobs.err.notTerminal',
  RETRY_FUTILE: 'admin.settings.jobs.err.futile',
});

// ── 空壳能力的标注（第一百一十批）────────────────────────────────────────────
// `asset_review` 与 `chat` 在这一页上看起来像"可配置、在跑"的能力——它们不是：
// 前者全仓零调用方，后者唯一路由 `/llm/echo` 是 SB-001 时代的冒烟路由。风险不是
// 「有个没用的配置项」，是**有人会去调它然后等着看效果**，包括排查故障的人。
//
// 判据取服务端投影的 `wired` 位（由 defaults.h 的 OperationDefault 推出），
// **不是前端硬编码的名单**——那就是 DP-2：名单是那个问题的镜像，而镜像会漂。
// 缺字段（旧后端）读作 wired：不标注只是少一句提示，误标注却会让人以为一个正在跑
// 的步骤是死的，然后不去改它。
export const isUnwired = (op) => op?.wired === false;

// ── SB-189 片 1 ③（第二百四十批）：LLM 已计价花费的读法 ─────────────────────────
// 与后端 core/llm_spend.h 同一套纪律，这里是它在**人眼前**的最后一道：
//   · **不显示「总额」**——只显示「已计价」。叫 total 的数会被当成总额。
//   · 不完整时**必须同时端出两个计数**（没配价目的调用、没回用量的调用）。
//   · **`complete` 不单信服务端那一位**：它必须与两个计数一致；不一致时按「不完整」处理——
//     一个说「完整」、而计数说「有 3 次没计价」的回体，是两个互相矛盾的事实，取悲观的那个。
//   · **小额不得显示成 $0.00**：`toFixed(2)` 会把真实的 $0.004 印成 $0.00，读起来就是「免费」，
//     而那正是这张账最不能说的一句话。0 < x < 0.01 显示为「< $0.01」。
export function formatSpendUsd(usd) {
  const v = Number(usd);
  if (!Number.isFinite(v) || v <= 0) return '$0.00';
  if (v < 0.01) return '< $0.01';
  return `$${v.toFixed(2)}`;
}

export function spendView(spend) {
  if (!spend || typeof spend !== 'object') return { state: 'absent' };
  const n = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : 0);
  const caveats = [];
  if (n(spend.unpricedCalls) > 0) {
    caveats.push({ key: 'admin.settings.spend.caveat.unpriced', n: n(spend.unpricedCalls) });
  }
  if (n(spend.noUsageCalls) > 0) {
    caveats.push({ key: 'admin.settings.spend.caveat.noUsage',
                   n: n(spend.noUsageCalls), failed: n(spend.failedCalls) });
  }
  const rows = (Array.isArray(spend.byModel) ? spend.byModel : []).map((l) => ({
    modelId: String(l?.modelId ?? ''),
    calls: n(l?.calls),
    pricedUsdText: formatSpendUsd(l?.pricedUsd),
    unpricedCalls: n(l?.unpricedCalls),
    noUsageCalls: n(l?.noUsageCalls),
  }));
  return {
    state: 'ok',
    pricedUsdText: formatSpendUsd(spend.pricedUsd),
    calls: n(spend.calls),
    complete: spend.complete === true && caveats.length === 0,
    caveats,
    rows,
    basis: typeof spend.basis === 'string' ? spend.basis : '',
  };
}
